import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { formatarProducao } from '../utils/unidades';
import Metrica, { MetricaGrid } from '../components/ui/Metrica';
import EmptyState from '../components/ui/EmptyState';
import Estado from '../components/ui/Estado';
import Modal from '../components/ui/Modal';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/ui/Icon';
import { fmtBRL } from '../utils/format';
import { HOME_ATALHOS } from '../navigation';

function Atalho({ item }) {
  return (
    <Link to={item.to} className="atalho">
      <span className="atalho-icone"><Icon nome={item.icon} tamanho={24} /></span>
      <span className="atalho-label">{item.label}</span>
      <span className="atalho-desc">{item.desc}</span>
    </Link>
  );
}

const PASSOS = [
  {
    to: '/lavouras',
    acao: 'Cadastrar áreas',
    titulo: 'Cadastre suas áreas de cultivo',
    texto: <>Coloque aqui todas as áreas que você tem: <strong>lavouras e estufas</strong>,
      próprias ou arrendadas. É o cadastro que só se faz uma vez e serve para todas as
      safras seguintes.</>,
  },
  {
    to: '/safras',
    acao: 'Criar safra',
    titulo: 'Crie a safra e plante nas áreas',
    texto: <>A safra é o ciclo onde tudo se organiza. Vincule as áreas que você vai plantar
      <strong> nesta safra específica</strong>, informando a cultura e a área plantada.</>,
  },
  {
    to: '/insumos',
    acao: 'Registrar compra',
    titulo: 'Cadastre e gerencie os insumos',
    texto: <>Registre o que comprou, de quem e por quanto. Isso monta seu histórico de
      preços e deixa a despesa organizada para a hora de declarar o <strong>IRPF</strong>.</>,
  },
  {
    to: '/custos',
    acao: 'Lançar manejo',
    titulo: 'Lance o manejo da lavoura',
    texto: <>Aplicações, plantio, adubação, mão de obra, arrendamento, diesel — o que
      <strong> vai para a área</strong>. Pode usar o estoque de insumo ou informar o valor
      direto. O sistema calcula o <strong>custo por área e por unidade produzida</strong>.</>,
  },
  {
    to: '/armazens',
    acao: 'Cadastrar armazéns',
    titulo: 'Cadastre os armazéns',
    texto: <>Os locais onde você entrega a produção. Serve para saber
      <strong> quanto de cada safra está em cada armazém</strong>.</>,
  },
  {
    to: '/cargas',
    acao: 'Registrar colheita',
    titulo: 'Registre a colheita',
    texto: <>Lance a produção, seja <strong>diária</strong> ou <strong>por cargas</strong> de
      caminhão, na unidade que você usa de verdade.</>,
  },
  {
    to: '/vendas',
    acao: 'Registrar venda',
    titulo: 'Registre as vendas',
    texto: <>Quantidade, preço e para quem vendeu. O <strong>saldo em estoque</strong> desconta
      automaticamente.</>,
  },
  {
    to: '/resultado',
    acao: 'Ver relatórios',
    titulo: 'Acompanhe os resultados',
    texto: <>Lucro por safra e por área, gasto por categoria e produtividade
      comparada entre safras.</>,
  },
];

function CarrosselPassos() {
  const carrosselRef = useRef(null);

  const scroll = (direcao) => {
    if (!carrosselRef.current) return;
    const largura = carrosselRef.current.firstChild?.offsetWidth || 260;
    carrosselRef.current.scrollBy({ left: direcao * (largura + 16), behavior: 'smooth' });
  };

  return (
    <div className="carrossel">
      {PASSOS.length > 1 && (
        <>
          <button
            type="button"
            className="carrossel__seta carrossel__seta--esq"
            onClick={() => scroll(-1)}
            aria-label="Passo anterior"
          ><Icon nome="chevron-direita" tamanho={16} /></button>
          <button
            type="button"
            className="carrossel__seta carrossel__seta--dir"
            onClick={() => scroll(1)}
            aria-label="Próximo passo"
          ><Icon nome="chevron-direita" tamanho={16} /></button>
        </>
      )}

      <div ref={carrosselRef} className="carrossel__trilho">
        {PASSOS.map((p, idx) => (
          <div key={p.to} className="carrossel__passo">
            <div className="passo__numero">{idx + 1}</div>
            <div className="passo__titulo">{p.titulo}</div>
            <div className="passo__texto">{p.texto}</div>
            <Link to={p.to} className="passo-acao">
              {p.acao}
              <Icon nome="seta-direita" tamanho={13} />
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const [safras, setSafras] = useState([]);
  const [safraId, setSafraId] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erroSafras, setErroSafras] = useState(null);
  const [carregandoResultado, setCarregandoResultado] = useState(false);
  const [erroResultado, setErroResultado] = useState(null);
  const [videoAberto, setVideoAberto] = useState(false);

  const carregarSafras = useCallback(() => {
    setCarregando(true);
    setErroSafras(null);
    api.getSafras()
      .then(list => {
        setSafras(list || []);
        const abertas = (list || []).filter(s => s.status !== 'FECHADA');
        const escolhida = (abertas.length > 0 ? abertas : list || []).slice(-1)[0];
        if (escolhida) setSafraId(escolhida.id);
      })
      .catch(() => {
        setSafras([]);
        setErroSafras('Não foi possível carregar suas safras. Verifique sua conexão e tente novamente.');
      })
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregarSafras(); }, [carregarSafras]);

  const carregarResultado = useCallback(() => {
    if (!safraId) return;
    setCarregandoResultado(true);
    setErroResultado(null);
    api.getResultadoSafra(safraId)
      .then(r => setResultado(r))
      .catch(() => {
        setResultado(null);
        setErroResultado('Não foi possível carregar os números desta safra. Verifique sua conexão e tente novamente.');
      })
      .finally(() => setCarregandoResultado(false));
  }, [safraId]);

  useEffect(() => { carregarResultado(); }, [carregarResultado]);

  const semSafra = !carregando && !erroSafras && safras.length === 0;
  const safraAtual = safras.find(s => String(s.id) === String(safraId));
  const tomResultado = resultado?.resultadoBrutoBRL >= 0 ? 'positivo' : 'negativo';
  const semDados = resultado
    && !(resultado.receitaBrutaBRL > 0)
    && !(resultado.custoTotalInsumosBRL > 0)
    && !(resultado.totalProduzido > 0);

  return (
    <div>
      {videoAberto && (
        <Modal titulo="Como usar o AgriGest" onClose={() => setVideoAberto(false)} largura={600}>
          <p className="secao-sub">
            Deslize para ver a ordem em que as coisas se encaixam no sistema.
          </p>
          <CarrosselPassos />

          <div className="bloco-video">
            <p className="secao-sub">Ou assista o vídeo explicativo:</p>
            <video controls playsInline>
              <source src={process.env.PUBLIC_URL + '/tutorial-agrigest.mp4'} type="video/mp4" />
              Seu navegador não suporta vídeo HTML5.
            </video>
          </div>
        </Modal>
      )}

      <PageHeader
        titulo="Início"
        descricao={safraAtual ? `Você está vendo a safra ${safraAtual.descricao}.` : 'Um resumo da safra e atalhos para o dia a dia.'}
        acao={{ label: 'Como usar o sistema', icone: 'play', variante: 'secundaria', onClick: () => setVideoAberto(true) }}
      />

      <Estado
        carregando={carregando}
        erro={erroSafras}
        aoTentarNovamente={carregarSafras}
        skeleton="card"
        itens={4}
      >
        {semSafra ? (
        <div className="card">
          <EmptyState
            icone="safra"
            titulo="Bem-vindo ao AgriGest!"
            descricao="Você ainda não tem safra cadastrada. Veja o passo a passo de como começar, ou use os atalhos abaixo."
            acao={{ label: 'Criar primeira safra', to: '/safras' }}
          />
        </div>
      ) : safraAtual && (
        <div className="card">
          <div className="secao-titulo secao-titulo--divisor">
            <h2>{safraAtual.cultura || 'Safra'} — {safraAtual.descricao}</h2>
            {safraAtual.status === 'FECHADA' && <span className="selo selo--alerta">Fechada</span>}
          </div>

          {safras.length > 1 && (
            <div className="chips chips--filtro">
              <span className="chips-label">Safra:</span>
              {safras.map(s => (
                <button
                  key={s.id}
                  className={`chip ${String(s.id) === String(safraId) ? 'chip--ativo' : ''}`}
                  onClick={() => setSafraId(s.id)}
                >
                  {s.descricao}
                </button>
              ))}
            </div>
          )}

          <Estado
            carregando={carregandoResultado}
            erro={erroResultado}
            aoTentarNovamente={carregarResultado}
            skeleton="card"
            itens={2}
          >
            {semDados ? (
            <EmptyState
              compacto
              icone="novo-registro"
              titulo="Safra sem lançamentos"
              descricao="Vincule as áreas de cultivo e comece a lançar manejo e colheita para ver os números aqui."
              acao={{ to: '/safras', label: 'Ir para a safra' }}
            />
          ) : resultado && (
            <>
              <MetricaGrid min={130}>
                <Metrica label="Receita" value={fmtBRL(resultado.receitaBrutaBRL)} tom="receita"
                  dica="Total das vendas registradas nesta safra." />
                <Metrica label="Manejo / custos" value={fmtBRL(resultado.custoTotalInsumosBRL)} tom="custo"
                  dica="Aplicações, mão de obra e serviços lançados na lavoura." />
                <Metrica label="Resultado" value={fmtBRL(resultado.resultadoBrutoBRL)} tom={tomResultado} destaque
                  dica="Receita menos custos." />
                <Metrica
                  label="Produção"
                  value={resultado.totalProduzido > 0
                    ? formatarProducao(resultado.totalProduzido, resultado.unidadeProducao)
                    : '—'}
                  detalhe={resultado.unidadesMistas ? 'há outras unidades' : undefined}
                  dica="Quantidade colhida na unidade de maior volume." />
              </MetricaGrid>
              <div className="card__rodape-link">
                <Link to="/resultado" className="link-acao">
                  Ver resultado completo
                  <Icon nome="seta-direita" tamanho={14} />
                </Link>
              </div>
            </>
          )}
          </Estado>
        </div>
      )}
      </Estado>

      {!carregando && HOME_ATALHOS.map(g => (
        <section key={g.titulo} className="grupo-atalhos">
          <h2 className="grupo-atalhos__titulo">{g.titulo}</h2>
          <p className="grupo-atalhos__desc">{g.descricao}</p>
          <div className="atalho-grid">
            {g.itens.map(item => <Atalho key={item.to + item.label} item={item} />)}
          </div>
        </section>
      ))}

      <div className="rodape-suporte">
        <a href="https://wa.me/5554999487383?text=Ol%C3%A1!%20Gostaria%20de%20falar%20sobre%20o%20AgriGest"
          target="_blank" rel="noopener noreferrer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          Dúvidas ou problemas? Fale conosco
        </a>
      </div>
    </div>
  );
}
